import React, { useState, useRef, useEffect } from "react";
import { X, Send, Sparkles } from "lucide-react";
import { API } from "../lib/api";

const PERSONAS = [
  { key: "kai", name: "Kai", role: "General GRC", color: "bg-slate-800", short: "K" },
  { key: "priya", name: "Priya S.", role: "PDPA Expert", color: "bg-blue-600", short: "P" },
  { key: "rajan", name: "Rajan", role: "Money Flow", color: "bg-emerald-600", short: "R" },
  { key: "mei", name: "Mei", role: "MAS Regs", color: "bg-rose-600", short: "M" },
];

const QUICK_PROMPTS = {
  kai: ["Where do I start with COSO ERM?", "Compare ISO 31000 vs NIST RMF", "What is a risk appetite statement?"],
  priya: ["What are the 9 PDPA obligations?", "When do I notify PDPC of a breach?", "DPO appointment — is it mandatory?"],
  rajan: ["What is the S$NEER policy band today?", "Explain UPI–PayNow corridor caps", "How does MAS price-band signalling work?"],
  mei: ["Summarise MAS Notice 655", "What does TRM say on cloud outsourcing?", "Veritas FEAT principles — operational mapping"],
};

function PersonaSwitcher({ persona, onSelect }) {
  return (
    <div className="px-6 py-3 border-b border-slate-200 bg-slate-50">
      <div className="overline-muted mb-2">Choose your advisor</div>
      <div className="grid grid-cols-4 gap-2">
        {PERSONAS.map(p => (
          <button
            key={p.key}
            className="persona-btn flex-col gap-1 py-2"
            data-active={p.key === persona}
            onClick={() => onSelect(p.key)}
            data-testid={`persona-${p.key}`}
          >
            <div className={`${p.color} w-7 h-7 rounded-full text-white flex items-center justify-center text-[11px] font-semibold`}>{p.short}</div>
            <div className="text-[11px] font-semibold leading-tight">{p.name}</div>
            <div className="text-[9px] opacity-70 leading-tight">{p.role}</div>
          </button>
        ))}
      </div>
    </div>
  );
}

function AdvisorEmptyState({ currentPersona, persona, onPickPrompt }) {
  return (
    <div>
      <div className="text-sm text-slate-700 font-medium">
        Hi, I'm <span className="font-display font-semibold" style={{ color: "var(--rp-primary)" }}>{currentPersona.name}</span> — your {currentPersona.role.toLowerCase()} advisor.
      </div>
      <div className="text-xs text-slate-500 mt-1">Educational responses only. Not legal or regulatory advice.</div>
      <div className="overline-muted mt-6 mb-3">Try a quick prompt</div>
      <div className="flex flex-col gap-2">
        {QUICK_PROMPTS[persona].map(q => (
          <button
            key={q}
            className="text-left text-sm border border-slate-200 px-3 py-2 rounded-sm hover:border-slate-400 hover:bg-slate-50 transition-colors"
            onClick={() => onPickPrompt(q)}
            data-testid="advisor-quick-prompt"
          >
            {q}
          </button>
        ))}
      </div>
    </div>
  );
}

async function streamChat({ sessionId, persona, text, onDelta }) {
  const res = await fetch(`${API}/ai/chat`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ session_id: sessionId, persona, message: text }),
  });
  if (!res.body) throw new Error("No stream body");
  const reader = res.body.getReader();
  const decoder = new TextDecoder();
  let acc = "";
  while (true) {
    const { done, value } = await reader.read();
    if (done) break;
    acc += decoder.decode(value, { stream: true });
    onDelta(acc);
  }
}

export default function AIAdvisor({ open, onClose }) {
  const [persona, setPersona] = useState("kai");
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const [sessionId] = useState(() => `sg-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`);
  const scrollRef = useRef(null);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages]);

  const send = async () => {
    const text = input.trim();
    if (!text || busy) return;
    setInput("");
    const userMsg = { role: "user", text, persona };
    const botMsg = { role: "bot", text: "", persona, streaming: true };
    setMessages(prev => [...prev, userMsg, botMsg]);
    setBusy(true);
    const patchLast = (patch) => setMessages(prev => {
      const out = [...prev];
      out[out.length - 1] = { ...out[out.length - 1], ...patch };
      return out;
    });
    try {
      await streamChat({ sessionId, persona, text, onDelta: (acc) => patchLast({ text: acc }) });
      patchLast({ streaming: false });
    } catch (e) {
      patchLast({ text: `Network error: ${e.message}. Please try again.`, streaming: false });
    } finally {
      setBusy(false);
    }
  };

  const onKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      send();
    }
  };

  const currentPersona = PERSONAS.find(p => p.key === persona);

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-[60]" data-testid="advisor-overlay" role="dialog" aria-modal="true">
      <div className="absolute inset-0 bg-slate-900/40 backdrop-blur-sm" onClick={onClose} />
      <div className="absolute right-0 top-0 bottom-0 w-full max-w-lg bg-white border-l border-slate-200 shadow-2xl flex flex-col fade-in" data-testid="advisor-panel">
        {/* Header */}
        <div className="px-6 py-4 border-b border-slate-200 flex items-center justify-between force-dark" style={{ background: "#0B1120" }}>
          <div className="flex items-center gap-3 text-white">
            <Sparkles size={18} />
            <div>
              <div className="font-display font-semibold text-base">AI Advisor</div>
              <div className="text-[11px] opacity-80 font-mono">Claude Sonnet 4.5 · Singapore desk</div>
            </div>
          </div>
          <button onClick={onClose} className="text-white/80 hover:text-white p-1" data-testid="advisor-close">
            <X size={20} />
          </button>
        </div>

        {/* Persona switcher */}
        <PersonaSwitcher persona={persona} onSelect={setPersona} />

        {/* Messages */}
        <div ref={scrollRef} className="flex-1 overflow-y-auto px-6 py-5 space-y-4" data-testid="advisor-messages">
          {messages.length === 0 && (
            <AdvisorEmptyState currentPersona={currentPersona} persona={persona} onPickPrompt={setInput} />
          )}
          {messages.map((m, i) => (
            <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
              <div className={`max-w-[88%] ${m.role === "user" ? "bg-slate-100 text-slate-900" : "bg-white border border-slate-200"} rounded-sm px-4 py-3`}>
                {m.role === "bot" && (
                  <div className="overline-muted mb-1">{(PERSONAS.find(p => p.key === m.persona) || currentPersona).name}</div>
                )}
                <div className={`text-sm leading-relaxed whitespace-pre-wrap ${m.streaming ? "streaming-cursor" : ""}`}>{m.text || (m.streaming ? "" : "")}</div>
              </div>
            </div>
          ))}
        </div>

        {/* Input */}
        <div className="border-t border-slate-200 p-4 bg-white">
          <div className="flex items-end gap-2">
            <textarea
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={onKeyDown}
              placeholder={`Ask ${currentPersona.name} about ${currentPersona.role.toLowerCase()}...`}
              rows={2}
              className="flex-1 resize-none border border-slate-200 rounded-sm px-3 py-2 text-sm focus:outline-none focus:border-slate-400"
              data-testid="advisor-input"
            />
            <button
              onClick={send}
              disabled={busy || !input.trim()}
              className="btn-primary disabled:opacity-50 disabled:cursor-not-allowed"
              data-testid="advisor-send"
            >
              <Send size={14} />
            </button>
          </div>
          <div className="text-[10px] text-slate-400 mt-2 font-mono">⌘+Enter to send · Sessions are ephemeral</div>
        </div>
      </div>
    </div>
  );
}
